import * as Sentry from '@sentry/browser';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createFragmentContainer, graphql } from 'react-relay';

import styled from 'styled-components';

import { usePageView } from '@feedback/hooks';

import { createQueryRenderer } from '../../relay';

import shouldUseSentry from '../../router/utils/shouldUseSentry';

import drawRoutes from '../../router/utils/drawRoutes';
import { media } from '../../utils/style';
import Menu from '../common/menu/Menu';

import layout from '../../app/layout';

import routeTo from '../../router/utils/routeTo';
import { RouteType } from '../../router/utils/types';

import DomainNotFound from '../../router/middlewares/DomainNotFound';
import { useAuthenticated } from '../../router/middlewares/useAuthenticated';
import { useRequiredLoginFields } from '../../router/middlewares/useRequiredLoginFields';
import { useDomainName } from '../../router/middlewares/useDomainName';
import { getDomainName } from '../../security/authentication';

import Sidebar from './sidebar/Sidebar';
import { DashboardTemplate_query } from './__generated__/DashboardTemplate_query.graphql';
import { menuItems } from './menuItems';
import { SidebarContext } from './SidebarContext';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  max-width: 100%;
`;

const ContentWrapper = styled.div`
  justify-content: center;
  flex-grow: 1;

  ${media.small`
    padding-top: 0;
    padding-bottom: 0;
  `};
`;

const Content = styled.div`
  position: absolute;
  display: flex;
  max-width: 100%;
  min-height: 100%;
  width: ${layout.content.width};
`;

type Props = {
  query: DashboardTemplate_query;
  routes: RouteType[];
};

const DashboardTemplate = (props: Props) => {
  const [isSidebarVisible, setIsSidebarVisible] = useState<boolean>(false);

  const { query, history, routes } = props;
  const { me, company } = query;

  const { t } = useTranslation();

  usePageView();
  const validDomainName = useDomainName(company);
  const isAuthenticated = useAuthenticated(me);
  const hasRequiredFields = useRequiredLoginFields(me);

  useEffect(() => {
    if (shouldUseSentry) {
      // https://docs.sentry.io/clients/javascript/usage/#tracking-users
      if (me) {
        Sentry.configureScope((scope) => {
          scope.setUser({
            id: me._id,
            name: me.name,
            email: me.email,
            companyId: company._id,
            companyName: company.name,
          });
          scope.setTag('companyId', company._id);
          scope.setTag('companyName', company.name);
        });
      }
    }
  }, [me?._id]);

  if (!validDomainName) {
    return <DomainNotFound domainName={getDomainName()} />;
  }

  if (!isAuthenticated) {
    return null;
  }

  if (hasRequiredFields) {
    return null;
  }

  const toggleSidebar = () => {
    setIsSidebarVisible(!isSidebarVisible);
  };

  const onSearch = (values) => {
    return history.push(routeTo('public.search', { search: values.search }));
  };

  return (
    <SidebarContext.Provider
      value={{
        isSidebarVisible,
        toggleSidebar,
      }}
    >
      <Wrapper>
        <Menu query={query} onSearch={onSearch} items={menuItems(t)} />
        <ContentWrapper>
          <Content>
            <Sidebar visible={isSidebarVisible} query={query} {...props} />
            {drawRoutes(routes)}
          </Content>
        </ContentWrapper>
      </Wrapper>
    </SidebarContext.Provider>
  );
};

const DashboardTemplateFragmentContainer = createFragmentContainer(
  DashboardTemplate,
  {
    query: graphql`
      fragment DashboardTemplate_query on Query {
        me {
          _id
          name
          email
          ...useAuthenticated_candidate @relay(mask: false)
        }
        company: companyDomainName {
          ...useDomainName_company @relay(mask: false)
        }
        ...Menu_query
        ...Sidebar_query
      }
    `,
  },
);

export default createQueryRenderer(DashboardTemplateFragmentContainer, {
  query: graphql`
    query DashboardTemplateQuery {
      ...DashboardTemplate_query
    }
  `,
  loadingView: null,
  fetchPolicy: 'network-only',
});
